import React, { useState, useEffect, useRef, useMemo } from "react";
import { useConfirm } from "material-ui-confirm";
import { useDispatch, useSelector } from "react-redux";
import { useBeforeunload } from "react-beforeunload";

import { Typography, Box, Card, Button } from "@mui/material";

import {
  sortCountriesBy,
  getTimeDisplay,
  isCorrectAnswer,
} from "../../../helpers";
import { handleExitGame } from "../../../helpers/handleExitGame";
import { listGameConfig } from "../../../data/index.js";
import { ACCEPTED_COUNTRY_NAMES } from "../../../data/ACCEPTED_COUNTRY_NAMES";
import GameHeader from "../GameHeader";
import GameResults from "../GameResults";
import AnswerSheet from "./AnswerSheet";
import TextInput from "../TextInput";

const Lists = ({ game }) => {
  useBeforeunload((e) => e.preventDefault());
  const confirm = useConfirm();
  const dispatch = useDispatch();
  const timeLeft = useRef(listGameConfig(game.listCategory)[game.region].time);
  const timerId = useRef();

  const [timeDisplay, setTimeDisplay] = useState(
    getTimeDisplay(listGameConfig(game.listCategory)[game.region].time)
  );
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [inputValue, setInputValue] = useState("");
  const [foundAnswers, setfoundAnswers] = useState([]);
  const [revealAnswers, setRevealAnswers] = useState(false);

  let countryData = useSelector((state) => state.game.game.countryData);

  useEffect(() => {
    let timer = setInterval(() => {
      setTimeDisplay(getTimeDisplay(--timeLeft.current));
    }, 1000);

    timerId.current = timer;

    if (timeLeft.current === 0) {
      handleEndGame();
    }

    return () => clearInterval(timer);
  }, [timeDisplay]);

  const countryList = useMemo(() => {
    let countriesSortedByCategory = sortCountriesBy(
      countryData,
      game.listCategory
    );

    return countriesSortedByCategory.slice(
      0,
      listGameConfig(game.listCategory)[game.region].listLength
    );
  }, [game, countryData]);

  const handleGiveUp = () => {
    confirm({
      description: "Answers will be revealed and game will end",
      confirmationButtonProps: { autoFocus: true },
    }).then(() => {
      if (
        ![
          "http://localhost:3000/",
          "https://geogames-dpocsai.netlify.app/",
        ].includes(window.location.href)
      ) {
        handleEndGame();
      }
    });
  };

  const scrollToAnswer = (answer) => {
    const LINE_HEIGHT = 9;
    let topOfAnswerList = document.getElementById("answers").offsetTop;
    let positionOfCorrectAnswer = document.getElementById(answer).offsetTop;
    document.getElementById("answers").scrollTop =
      positionOfCorrectAnswer - topOfAnswerList - LINE_HEIGHT;
  };

  const handleCorrectAnswer = (answer) => {
    document.getElementById(answer).innerText += ` ${answer}`;
    let inputBox = document.getElementById("inputBox");
    inputBox.style.border = "2px solid #729601";
    setfoundAnswers([...foundAnswers, answer]);
    setTimeout(() => {
      inputBox.style.border = "2px solid transparent";
      setInputValue("");
      setProgress(progress + 1);

      if (progress >= countryList.length - 1) {
        handleEndGame();
      }
    }, 300);
  };

  const handleSubmitCountry = (inputValue, setInputValue) => {
    inputValue = inputValue.trim();

    let answer = countryList.find((country) =>
      isCorrectAnswer(inputValue, country, game.title)
    );

    if (answer && !foundAnswers.includes(answer.name)) {
      handleCorrectAnswer(answer.name, setInputValue);
      scrollToAnswer(answer.name);
    }

    if (
      !answer &&
      game.suddenDeath &&
      ACCEPTED_COUNTRY_NAMES.includes(inputValue)
    ) {
      handleEndGame();
    }
  };

  const handleEndGame = () => {
    clearInterval(timerId.current);
    setTimeDisplay((timeLeft.current = 0));
    document.getElementById("inputBox").disabled = true;
    document.getElementById("giveUpBtn").disabled = true;
    if (game.suddenDeath) {
      document.getElementById("submitBtn").disabled = true;
    }

    setRevealAnswers(true);
    setOpen(true);
  };
  const handleKeyDown = () => {
    document.getElementById("submitBtn").click();
  };

  return (
    <>
      <Box
        elevation={5}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          margin: "0 auto",
          padding: "0.4rem 0",
          width: "100%",
          backgroundColor: "background.default",
          textAlign: "center",
        }}
      >
        <Typography
          variant={`${window.innerWidth < 600 ? "h5" : "h4"}`}
          fontWeight="bold"
        >
          {game.region}
        </Typography>
        <Typography variant={`${window.innerWidth < 600 ? "subtitle" : "h6"}`}>
          {game.listCategory}
        </Typography>
      </Box>
      <Card
        sx={{
          width: "90%",
          backgroundColor: "background.paper",
          margin: "auto",
          padding: "0 5px",
          maxWidth: "700px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "1rem",
        }}
        elevation={5}
      >
        <GameHeader
          score={timeDisplay}
          progress={progress}
          totalQuestions={countryList.length}
          handleExitGame={() => {
            handleExitGame(confirm, dispatch);
          }}
        />
        <>
          <AnswerSheet
            countryList={countryList}
            revealAnswers={revealAnswers}
            foundAnswers={foundAnswers}
          />
          <form onSubmit={(e) => e.preventDefault()}>
            <Box
              sx={{
                margin: "1rem auto",
                width: { xs: "90%", sm: "70%", md: "60%" },
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "1rem",
              }}
            >
              <TextInput
                setInputValue={setInputValue}
                inputValue={inputValue}
                handleKeyDown={handleKeyDown}
              />
              <Button
                id="submitBtn"
                variant="contained"
                size="small"
                type="submit"
                sx={{
                  width: "25%",
                  padding: "0.3rem",
                }}
                onClick={() => {
                  if (inputValue.trim().length < 1) {
                    return;
                  }
                  handleSubmitCountry(inputValue, setInputValue);
                }}
              >
                Enter
              </Button>
            </Box>
          </form>
          <Button
            id="giveUpBtn"
            size="small"
            sx={{
              width: "25%",
              color: "gray",
              fontSize: "10px",
              margin: "0 auto 1rem auto",
            }}
            onClick={() => {
              handleGiveUp();
            }}
          >
            Give Up
          </Button>
        </>
      </Card>
      <GameResults
        open={open}
        setOpen={setOpen}
        game={{ ...game, questions: countryList.length }}
        score={progress}
      />
    </>
  );
};

export default Lists;
